import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import TipoProdutoFilterBar from '../../components/TipoProdutoFilterBar';
import { tipoProdutoService } from '../../services/tipoProdutoService';
import { withPermissionAndLevel } from '../../components/withPermission';

const columns = [
  { Header: 'ID', accessor: 'idTipoProduto' },
  { Header: 'Nome', accessor: 'nome' },
  { Header: 'Status', accessor: 'status' },
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Alterado Por', accessor: 'alteradoPor' },
  { Header: 'Cadastrado Em', accessor: 'cadastradoEm', Cell: value => {
    if (!value) return '';
    const date = new Date(value);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
  { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => {
    if (!value) return '';
    const date = new Date(value);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
];

const pageSizes = [5, 10, 20];

const TipoProdutoList = () => {
  const [tipoProdutos, setTipoProdutos] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ nome: '', status: '', cadastradoPor: '', alteradoPor: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const loadTipoProdutos = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: pageSize,
          search: filters.nome
        };

        // Só adiciona o status se não estiver vazio
        if (filters.status && filters.status.trim() !== '') {
          params.status = filters.status;
        }

        const result = await tipoProdutoService.list(params);
        setTipoProdutos(result.tiposProduto || []);
        setPagination(result.pagination || { total: 0, totalPages: 1 });
      } catch (err) {
        console.error('Erro ao carregar tipos de produto:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTipoProdutos();
  }, [page, pageSize, filters]);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Filtragem (removida pois o backend já filtra)
  const filteredTipoProdutos = tipoProdutos;

  // Paginação (usa dados do backend)
  const totalPages = pagination.totalPages;
  const paginatedTipoProdutos = filteredTipoProdutos; // Remove o slice pois já vem paginado do backend

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Tipos de Produto</h2>
            <Link
              to="/tipo-produto/create"
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Novo Tipo</span>
            </Link>
          </div>
          <ColumnVisibilityModal
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />
          <div className="max-w-full">
            <TipoProdutoFilterBar filters={filters} onChange={handleFilterChange} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination currentPage={page} totalPages={totalPages} totalItems={pagination.total || 0} itemsPerPage={pageSize} onPageChange={handlePageChange} />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              {/* O total agora vem do próprio <Pagination> ("Mostrando X a Y de Z") */}
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>
          <div className="max-w-full">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <span className="ml-2 text-gray-600">Carregando...</span>
              </div>
            ) : (
              <Table
                columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                  if (col.accessor === 'nome') {
                    return {
                      ...col,
                      Cell: (value, row) => (
                        <div className="flex items-center gap-2">
                          <span>{value}</span>
                          <Link
                            to={`/tipo-produto/${row.idTipoProduto}/edit`}
                            className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                            title="Editar"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Link>
                        </div>
                      )
                    };
                  }
                  return col;
                })}
                data={paginatedTipoProdutos}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withPermissionAndLevel(TipoProdutoList, 'tipo_produto.list', 3, 'Você não tem permissão para acessar o módulo de Tipos de Produto.');
